import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography
} from '@mui/material';
import { GroupsOutlinedIcon } from 'icons/materialIcons';

const TeamV2CreateUnassignedContent = ({ onDataChange, onValidationChange }) => {
    const [memberCount, setMemberCount] = useState(0);
    const [isValid, setIsValid] = useState(false);

    useEffect(() => {
        const valid = memberCount > 0 && memberCount <= 100;
        setIsValid(valid);
        onValidationChange(valid);

        // Create mock data for the number of members
        const mockMembers = Array.from({ length: memberCount }, (_, index) => ({
            id: index + 1,
            firstName: '',
            lastName: '',
            jobTitle: '',
            company: ''
        }));
        onDataChange(valid ? mockMembers : []);
    }, [memberCount, onDataChange, onValidationChange]);

    const handleMemberCountChange = (e) => {
        const value = e.target.value;
        if (value === '') {
            setMemberCount(0);
        } else {
            const numValue = parseInt(value);
            if (!isNaN(numValue)) {
                setMemberCount(Math.max(0, Math.min(100, numValue)));
            }
        }
    };

    return (
        <Box sx={{ padding: '2rem', paddingTop: '3rem' }}>
            {/* Heading */}
            <Typography variant="body2" sx={{
                fontSize: '1.1rem',
                fontWeight: 600,
                color: '#4f4f4f',
                marginBottom: '1.25rem'
            }}>
                Create Unassigned Members
            </Typography>

            {/* Description */}
            <Typography variant="body1" sx={{
                marginBottom: '1.5rem',
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#828282',
                lineHeight: 1.5
            }}>
                You should create unassigned members if you want to share information that is not associated with an employee and therefore does not need an employee email. You can always assign these to an employee later.
            </Typography>

            {/* Input and Button Row */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1rem'
            }}>
                {/* Number Input */}
                <TextField
                    type="number"
                    value={memberCount === 0 ? '' : memberCount}
                    onChange={handleMemberCountChange}
                    placeholder="Enter number of members"
                    sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#f7f7f7',
                            borderRadius: '0.25rem',
                            '& fieldset': {
                                border: 'none',
                            },
                            '&:hover fieldset': {
                                border: 'none',
                            },
                            '&.Mui-focused fieldset': {
                                border: 'none',
                                outline: '0.125rem solid #1976d2',
                                outlineOffset: '-0.125rem'
                            },
                        },
                        '& .MuiInputBase-input': {
                            padding: '0.75rem',
                            fontSize: '0.8rem',
                            fontWeight: '300',
                            color: '#333',
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: '#A0A0A0',
                            fontWeight: '400',
                        }
                    }}
                />

                {/* Add Members Button */}
                <Button
                    variant="contained"
                    disabled={!isValid}
                    sx={{
                        backgroundColor: '#000',
                        color: '#fff',
                        fontWeight: 600,
                        borderRadius: '0.5rem',
                        textTransform: 'none',
                        padding: '0.625rem 1.5rem',
                        fontSize: '0.9rem',
                        '&:hover': {
                            backgroundColor: '#333',
                        },
                        '&:disabled': {
                            backgroundColor: '#e0e0e0',
                            color: '#999',
                        }
                    }}
                >
                    Add Members
                </Button>
            </Box>

            {/* Blue Summary Section */}
            {memberCount > 0 && (
                <Box sx={{
                    padding: '0.75rem 1rem',
                    backgroundColor: '#E3F2FD',
                    borderRadius: '0.5rem',
                    margin: '1rem 0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <GroupsOutlinedIcon sx={{
                            color: '#1976d2',
                            fontSize: '1rem',
                            marginRight: '0.75rem'
                        }} />
                        <Typography variant="body1" sx={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: '#828282',
                            lineHeight: 1.5
                        }}>
                            Adding {memberCount} member{memberCount !== 1 ? 's' : ''}
                        </Typography>
                    </Box>
                    <Typography variant="body1" sx={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#828282',
                        lineHeight: 1.5
                    }}>
                        This will influence your monthly payment
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default TeamV2CreateUnassignedContent;
